import { Box, Stepper, Step, StepLabel, useTheme, IconButton } from "@mui/material";
import { Questionnaire } from "../../models/types";
import { useEffect, useRef, useState } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

type QuestionnaireStepperProps = {
  steps: string[];
  questionnaire: Questionnaire;
  questionIndex: number;
};

export const QuestionnaireStepper = (props: QuestionnaireStepperProps) => {
  const { steps, questionnaire, questionIndex } = props;
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkForOverflow = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      const isOverflowing = container.scrollWidth > container.clientWidth;
      const scrollPosition = container.scrollLeft;

      setShowLeftArrow(scrollPosition > 0);
      setShowRightArrow(scrollPosition < container.scrollWidth - container.clientWidth - 5);
    }
  };

  useEffect(() => {
    checkForOverflow();
    const timer = setTimeout(checkForOverflow, 100);
    const finalTimer = setTimeout(checkForOverflow, 500);

    window.addEventListener("resize", checkForOverflow);
    return () => {
      window.removeEventListener("resize", checkForOverflow);
      clearTimeout(timer);
      clearTimeout(finalTimer);
    };
  }, [steps]);

  useEffect(() => {
    if (activeStepRef.current && containerRef.current) {
      const activeStep = activeStepRef.current;
      const container = containerRef.current;

      const activeStepLeft = activeStep.offsetLeft;
      const activeStepWidth = activeStep.offsetWidth;
      const containerWidth = container.clientWidth;

      // Calculate the scroll position to center the active step
      const scrollPosition = activeStepLeft - containerWidth / 2 + activeStepWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });

      setTimeout(checkForOverflow, 300);
    }
  }, [questionIndex]);

  const handleScroll = () => {
    checkForOverflow();
  };

  const scrollLeft = () => {
    containerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    containerRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  const activeStepIndex = steps?.indexOf(questionnaire.questions[questionIndex]?.sub_section || "");

  return (
    <Box
      width="100%"
      minWidth="300px"
      maxWidth="800px"
      display="flex"
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      position="relative"
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          position: "relative",
          paddingLeft: "40px",
          paddingRight: "40px",
        }}
      >
        {showLeftArrow && (
          <IconButton
            onClick={scrollLeft}
            size="small"
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 2,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.background.paper,
                "& .MuiSvgIcon-root": {
                  color: (theme) => (showLeftArrow ? theme.palette.custom.hyperlink : "inherit"),
                },
              },
              width: 32,
              height: 32,
              minWidth: 32,
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            position: "relative",
            width: "100%",
          }}
        >
          {showLeftArrow && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "80px",
                background: `linear-gradient(to right, ${theme.palette.background.paper} 0%, transparent 100%)`,
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}

          <Box
            ref={containerRef}
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              scrollBehavior: "smooth",
              flexWrap: "nowrap",
              width: "100%",
            }}
            onScroll={handleScroll}
          >
            <Stepper activeStep={activeStepIndex}>
              {steps?.map((label, index) => (
                <Step
                  active={label === questionnaire.questions[questionIndex]?.sub_section}
                  key={index}
                  ref={index === activeStepIndex ? activeStepRef : undefined}
                >
                  <StepLabel
                    sx={{ textAlign: "center" }}
                    color={theme.palette.custom.blueTypography}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {showRightArrow && (
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: "80px",
                background: `linear-gradient(to left, ${theme.palette.background.paper} 0%, transparent 100%)`,
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
        </Box>
        {showRightArrow && (
          <IconButton
            onClick={scrollRight}
            size="small"
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 2,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.background.paper,
                "& .MuiSvgIcon-root": {
                  color: (theme) => theme.palette.custom.hyperlink,
                },
              },
              width: 32,
              height: 32,
              minWidth: 32,
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
